import React, { useRef, useEffect, useCallback, useState } from 'react';
import { useStableAnimation } from '../../hooks/useStableAnimation';
import type { TransitionWrapperProps } from './TransitionWrapper.types';

interface KeyframeStyles {
  [property: string]: string | number;
}

export interface Keyframes {
  [percent: string]: KeyframeStyles;
}

/**
 * Internal phase of the transition:
 * - 'unmounted' → not rendered
 * - 'entering'  → running enter animation
 * - 'entered'   → fully visible
 * - 'exiting'   → running exit animation
 */
type TransitionPhase = 'unmounted' | 'entering' | 'entered' | 'exiting';

/**
 * TransitionWrapper
 *
 * A low-level transition primitive that handles enter/exit animations
 * for a single child using the Web Animations API.
 *
 * It is *not* opinionated about layout; it only animates the node it's given.
 * Components like Drawer, Dialog, Popover, etc. can build on top of this.
 */
export const TransitionWrapper = ({
  isOpen,
  enterAnimation: enterAnimationProp,
  exitAnimation: exitAnimationProp,
  duration = '0.3s',
  easing = 'cubic-bezier(0.4, 0, 0.2, 1)',
  shouldMountOnEnter = false,
  shouldUnmountOnExit = false,
  onEnter,
  onEntered,
  onExit,
  onExited,
  className,
  style,
  children,
}: TransitionWrapperProps) => {
  const nodeRef = useRef<HTMLDivElement | null>(null);
  const animationRef = useRef<Animation | null>(null);
  const onEnterRef = useRef(onEnter);
  onEnterRef.current = onEnter;
  const onEnteredRef = useRef(onEntered);
  onEnteredRef.current = onEntered;
  const onExitRef = useRef(onExit);
  onExitRef.current = onExit;
  const onExitedRef = useRef(onExited);
  onExitedRef.current = onExited;

  // enterAnimationProp and exitAnimationProp are required props, so the
  // hook will always resolve to a defined Keyframes object.
  const enterAnimation = useStableAnimation(enterAnimationProp) as Keyframes;
  const exitAnimation = useStableAnimation(exitAnimationProp) as Keyframes;

  // Initial phase
  const [phase, setPhase] = useState<TransitionPhase>(() => {
    if (!shouldMountOnEnter && isOpen) {
      return 'entered';
    }
    if (!shouldMountOnEnter && !isOpen) {
      return 'entered';
    } // visible but maybe styled off-screen
    if (shouldMountOnEnter && isOpen) {
      return 'entering';
    }
    return 'unmounted';
  });

  // Presence: rendered if not unmounted
  const isMounted = phase !== 'unmounted';

  // prefers-reduced-motion
  const isPrefersReducedMotion =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const makeKeyframes = useCallback(
    (animation: Keyframes) =>
      Object.entries(animation)
        .sort(([a, _a], [b, _b]) => Number.parseFloat(a) - Number.parseFloat(b))
        .map(([percent, styles]) => ({
          offset: Number.parseFloat(percent) / 100,
          ...styles,
        })) as (Keyframe & KeyframeStyles)[],
    []
  );

  const parseDurationMs = useCallback((value: string): number => {
    if (value.endsWith('ms')) {
      return Number.parseFloat(value);
    }
    // Assume seconds
    return Number.parseFloat(value) * 1000;
  }, []);

  // 1. Phase transitions based on `isOpen`
  useEffect(() => {
    setPhase((current) => {
      // If consumer wants it visible
      if (isOpen) {
        if (current === 'unmounted') {
          return shouldMountOnEnter ? 'entering' : 'entered';
        }
        if (current === 'exiting') {
          return 'entering';
        }
        return current; // entering/entered stay
      }

      // isOpen === false
      if (!isOpen) {
        if (current === 'entering' || current === 'entered') {
          return 'exiting';
        }
        return current; // exiting/unmounted stay
      }

      return current;
    });
  }, [isOpen, shouldMountOnEnter]);

  // 2. Run animations when phase changes
  useEffect(() => {
    const node = nodeRef.current;
    if (!node) {
      return;
    }

    // Cancel any in-flight animation
    if (animationRef.current) {
      animationRef.current.cancel();
      animationRef.current = null;
    }

    // ENTERING
    if (phase === 'entering') {
      onEnterRef.current?.();

      if (isPrefersReducedMotion) {
        const frames = makeKeyframes(enterAnimation);
        const finalFrame = frames[frames.length - 1] as
          | KeyframeStyles
          | undefined;
        if (finalFrame) {
          Object.assign(node.style, finalFrame);
        }
        onEnteredRef.current?.();
        setPhase('entered');
        return;
      }

      const frames = makeKeyframes(enterAnimation);
      const durationMs = parseDurationMs(duration);

      const instance = node.animate(frames, {
        duration: durationMs,
        easing,
        fill: 'forwards',
      });
      animationRef.current = instance;

      instance.onfinish = () => {
        const finalFrame = frames[frames.length - 1] as
          | KeyframeStyles
          | undefined;
        instance.cancel();
        if (finalFrame) {
          Object.assign(node.style, finalFrame);
        }
        onEnteredRef.current?.();
        setPhase('entered');
        animationRef.current = null;
      };

      return;
    }

    // EXITING
    if (phase === 'exiting') {
      onExitRef.current?.();

      if (isPrefersReducedMotion) {
        const frames = makeKeyframes(exitAnimation);
        const finalFrame = frames[frames.length - 1] as
          | KeyframeStyles
          | undefined;
        if (finalFrame) {
          Object.assign(node.style, finalFrame);
        }
        onExitedRef.current?.();
        setPhase(shouldUnmountOnExit ? 'unmounted' : 'entered');
        return;
      }

      const frames = makeKeyframes(exitAnimation);
      const durationMs = parseDurationMs(duration);

      const instance = node.animate(frames, {
        duration: durationMs,
        easing,
        fill: 'forwards',
      });
      animationRef.current = instance;

      instance.onfinish = () => {
        const finalFrame = frames[frames.length - 1] as
          | KeyframeStyles
          | undefined;
        instance.cancel();
        if (finalFrame) {
          Object.assign(node.style, finalFrame);
        }
        onExitedRef.current?.();
        setPhase(shouldUnmountOnExit ? 'unmounted' : 'entered');
        animationRef.current = null;
      };
    }
  }, [
    phase,
    duration,
    easing,
    enterAnimation,
    exitAnimation,
    isPrefersReducedMotion,
    makeKeyframes,
    parseDurationMs,
    shouldUnmountOnExit,
  ]);

  if (!isMounted) {
    return null;
  }

  return (
    <div ref={nodeRef} className={className} style={style}>
      {children}
    </div>
  );
};
